import { useCallback, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Button, Input, InputDateMonth, InputMask } from '~/components';
import { useAuth } from '~/hooks/auth';
import { UserDataStateLocation } from '~/models';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';
import { regex_remove_all_non_digits } from '~/utils/regex';

import { SignUpCompleteFormData } from '../SignUpComplete';

import { Container, Wrapper, Title } from './styles';

interface CreditCardFormData {
  holder_name: string;
  card_expiration: string;
  card_number: string;
  card_cvv: string;
  registry_code: string;
}

const SignUpComplete = () => {
  const { signInProfessional } = useAuth();
  const { t } = useTranslation(['Yup', 'Toast', 'Professional:SignUpComplete']);

  const {
    state: { userData, completeSignUpData },
  } = useLocation<{
    userData: UserDataStateLocation;
    completeSignUpData: SignUpCompleteFormData;
  }>();

  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (data: CreditCardFormData) => {
      try {
        setLoading(true);

        const schema = Yup.object().shape({
          holder_name: Yup.string().required(t('Yup:holder_name_required')),
          card_expiration: Yup.string().required(
            t('Yup:card_expiration_required'),
          ),
          card_number: Yup.string().required(t('Yup:card_number_required')),
          card_cvv: Yup.string().required(t('Yup:card_cvv_required')),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const {
          card_number,
          card_expiration,
          registry_code,
          card_cvv,
          holder_name,
        } = data;
        const card_number_only_numbers = card_number.replace(
          regex_remove_all_non_digits,
          '',
        );
        const registry_code_formated = registry_code.replace(
          regex_remove_all_non_digits,
          '',
        );
        const card_expiration_formated = card_expiration
          .split('-')
          .reverse()
          .join()
          .replace(',', '/');

        api.defaults.headers.authorization = `Bearer ${userData.data.token}`;

        const backupCPF = completeSignUpData?.cpf;

        delete completeSignUpData?.cpf;
        delete completeSignUpData?.name;

        const routeData = {
          ...completeSignUpData,
          card_cvv,
          holder_name,
          card_number: card_number_only_numbers,
          registry_code: registry_code_formated,
          card_expiration: card_expiration_formated,
        };

        await api.put(
          `/professional/${userData.user.professional.id}/completeProfile`,
          routeData,
        );

        await signInProfessional({
          cpf: backupCPF || '',
          password: userData.passwordToSignIn,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        }

        toast.error(t('Toast:error'));
      } finally {
        setLoading(false);
      }
    },
    [
      completeSignUpData,
      signInProfessional,
      t,
      userData.data.token,
      userData.passwordToSignIn,
      userData.user.professional.id,
    ],
  );

  // console.log('userData:', userData);
  // console.log('completeSignUpData:', completeSignUpData);

  // eslint-disable-next-line no-return-assign
  return (
    <Container>
      <Wrapper>
        <Title>
          {t('Professional:SignUpComplete:billing_information_title')}
        </Title>

        <h3>{t('Professional:SignUpComplete:plan_information_title')}</h3>
        <p>
          {t('Professional:SignUpComplete:plan_information')}{' '}
          <strong>
            {t('Professional:SignUpComplete:plan_information_total_students')}
          </strong>
        </p>

        <>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <InputMask
              name="card_number"
              placeholder={t('Professional:SignUpComplete:card_number')}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
              mask="9999 9999 9999 9999"
            />
            <Input
              name="holder_name"
              placeholder={t('Professional:SignUpComplete:holder_name')}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <InputDateMonth
              name="card_expiration"
              placeholder={t('Professional:SignUpComplete:card_expiration')}
              style={{ fontSize: 16 }}
            />

            <InputMask
              name="card_cvv"
              placeholder={t('Professional:SignUpComplete:card_cvv')}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
              mask="999"
            />

            <InputMask
              name="registry_code"
              placeholder={t('Professional:SignUpComplete:registry_code')}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
              mask="999.999.999-99"
            />
          </Form>
        </>

        <Button
          onClick={() => formRef.current?.submitForm()}
          style={{ maxWidth: 387 }}
          loading={loading}
        >
          {t('Professional:SignUpComplete:payment_continue')}
        </Button>
      </Wrapper>
    </Container>
  );
};

export default SignUpComplete;
